import { useState, useEffect, useCallback, useRef } from 'react';

/**
 * Hook to detect when the browser tab is in background (hidden).
 * Used to pause polling and other heavy operations when user is not viewing the tab.
 *
 * This helps reduce server load when users have multiple tabs open.
 */
export function useBackgroundPause() {
  const [isBackground, setIsBackground] = useState(() =>
    typeof document !== 'undefined' ? document.hidden : false
  );
  const [lastActiveTime, setLastActiveTime] = useState<number>(Date.now());
  const callbacksRef = useRef<Set<() => void>>(new Set());

  // Handle visibility change
  useEffect(() => {
    if (typeof document === 'undefined') return;

    const handleVisibilityChange = () => {
      const hidden = document.hidden;
      setIsBackground(hidden);

      if (!hidden) {
        // Tab became visible - update last active time
        setLastActiveTime(Date.now());
        // Trigger all registered callbacks
        callbacksRef.current.forEach(callback => callback());
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Register a callback to be called when tab becomes visible
  const onBecomeVisible = useCallback((callback: () => void) => {
    callbacksRef.current.add(callback);
    return () => {
      callbacksRef.current.delete(callback);
    };
  }, []);

  // Get time since tab was last active (useful for deciding if data is stale)
  const getTimeSinceActive = useCallback(() => {
    return Date.now() - lastActiveTime;
  }, [lastActiveTime]);

  return {
    isBackground,
    isVisible: !isBackground,
    lastActiveTime,
    getTimeSinceActive,
    onBecomeVisible,
  };
}

/**
 * Hook that provides a smart interval that pauses when tab is in background.
 * Automatically resumes and optionally triggers immediately when tab becomes visible.
 */
export function useSmartInterval(
  callback: () => void,
  intervalMs: number,
  options: {
    enabled?: boolean;
    pauseInBackground?: boolean;
    triggerOnVisible?: boolean;
  } = {}
) {
  const {
    enabled = true,
    pauseInBackground = true,
    triggerOnVisible = true,
  } = options;

  const { isBackground, onBecomeVisible } = useBackgroundPause();
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const callbackRef = useRef(callback);

  // Keep callback ref updated
  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  // Manage interval
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    // Don't start if disabled
    if (!enabled) return;

    // Don't start if in background and pauseInBackground is true
    if (isBackground && pauseInBackground) return;

    // Start interval
    intervalRef.current = setInterval(() => {
      callbackRef.current();
    }, intervalMs);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [enabled, isBackground, pauseInBackground, intervalMs]);

  // Trigger callback when becoming visible
  useEffect(() => {
    if (!triggerOnVisible || !enabled) return;

    return onBecomeVisible(() => {
      // Small delay to let other state settle
      setTimeout(() => {
        callbackRef.current();
      }, 100);
    });
  }, [onBecomeVisible, triggerOnVisible, enabled]);
}
